import React, { useState, useEffect, useCallback, Fragment } from "react";
import { useContacts } from "hooks/useContacts";
import {
  PhoneIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  XMarkIcon,
  PlusIcon,
  StarIcon as StarOutlineIcon,
  ClockIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { StarIcon as StarSolidIcon, PencilIcon } from "@heroicons/react/24/solid";
import { Popover, PopoverButton, PopoverPanel, Transition } from "@headlessui/react";
import { useLeadOptimisticState } from 'contexts/LeadOptimisticStateContext';
import { addLeadNote } from "services/notesService";
import { getLead, counselorVerifyPhoneEmail, bulkUpdateLeadStage, updateLeadOwner } from "services/leadsService";
import { toast } from "sonner";
import { transformLeadDetailsApiData } from "utils/leadDataTransforms";
import { formatCostAustralian, isCostOrRevenueField } from "utils/formatCost";
import { upsertLeadTags } from "services/leadTagsService";
import { LeadTagSelector } from "components/shared/form/LeadTagSelector";
import { getInternalUsersForAssigningLeads } from "services/userService";
import CallModal from "../LeadActions/CallModal";
import EmailMessageModal from "../LeadActions/EmailMessageModal";
import SMSMessageModal from "../LeadActions/SMSMessageModal";
import FollowUpScheduleModal from "../LeadActions/FollowUpScheduleModal";
import { useActivityTypes } from "hooks/useFollowUp";
import { useMediaQuery } from "hooks/useMediaQuery";
import { useClickOutside } from "hooks/useClickOutside";
import { invalidateTimelineCache } from "hooks/useTimeline";
import { invalidateNotesCache } from "hooks/useNotes";
import TimelineView from "components/leads/LeadDetails/TimeLineView";
import LeadDetailsForm from "components/leads/LeadDetails/LeadForm";
import NotesTab from "components/leads/LeadDetails/NotesTab";
import ClientNotesTab from "components/shared/ClientNotes/ClientNotesTab";
import DocumentsTab from "components/leads/LeadDetails/DocumentsTab/DocumentsTab";
import LeadManagementTab from "components/leads/LeadDetails/LeadManagementTab";
import CalendarTab from "components/leads/LeadDetails/CalendarTab";
import CommunicationsTab from "components/leads/LeadDetails/CommunicationsTab";
import QuickLeadModal from "../LeadActions/QuickLeadModal";
import StageChangeModal from "../LeadActions/StageChangeModal";
import { shouldTriggerJobConversion } from "services/leadStageService";
import { shouldWarnJobCancellation, shouldOfferJobReactivation } from "utils/leadStageHelpers";
import { ConvertToJobModal } from "app/pages/jobs/components/ConvertToJobModal";
import { SetJobStatusModal } from "app/pages/jobs/components/SetJobStatusModal";
import { getJobByLeadId } from "services/jobsService";
import { ConfirmModal } from "components/shared/ConfirmModal";
import SetPriorityModal from "../LeadActions/SetPriorityModal";
import SetInteractionLevelModal from "../LeadActions/SetInteractionLevelModal";
import AssignOwnerModal from "../LeadActions/AssignOwnerModal";
import { Circlebar, Button } from "components/ui";
import { calculatePriority } from "../../../utils/priorityCalculator";
import { useDocuments } from "./DocumentsTab/hooks/useDocuments";
import ContactsTab from "./ContactsTab";
import LeadDrawerContacts from "./LeadDrawerContacts";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { formatNumber } from "utils/formatNumber";
import {
  canCall,
  canSendSMS,
  canSendEmail,
  canCreateFollowUp,
  canAssignLead,
  canEditLead
} from "permissions/permissionHelpers";

const getInitials = (name) => {
  if (!name || typeof name !== "string") return "U";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "U";
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
};

const USER_COLORS = [
  'bg-red-50 text-red-600 border-red-100 dark:bg-red-950/20 dark:text-red-400 dark:border-red-900/30',
  'bg-orange-50 text-orange-600 border-orange-100 dark:bg-orange-950/20 dark:text-orange-400 dark:border-orange-900/30',
  'bg-amber-50 text-amber-600 border-amber-100 dark:bg-amber-950/20 dark:text-amber-400 dark:border-amber-900/30',
  'bg-yellow-50 text-yellow-600 border-yellow-100 dark:bg-yellow-950/20 dark:text-yellow-400 dark:border-yellow-900/30',
  'bg-lime-50 text-lime-600 border-lime-100 dark:bg-lime-950/20 dark:text-lime-400 dark:border-lime-900/30',
  'bg-green-50 text-green-600 border-green-100 dark:bg-green-950/20 dark:text-green-400 dark:border-green-900/30',
  'bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-900/30',
  'bg-teal-50 text-teal-600 border-teal-100 dark:bg-teal-950/20 dark:text-teal-400 dark:border-teal-900/30',
  'bg-cyan-50 text-cyan-600 border-cyan-100 dark:bg-cyan-950/20 dark:text-cyan-400 dark:border-cyan-900/30',
  'bg-sky-50 text-sky-600 border-sky-100 dark:bg-sky-950/20 dark:text-sky-400 dark:border-sky-900/30',
  'bg-blue-50 text-blue-600 border-blue-100 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-900/30',
  'bg-violet-50 text-violet-600 border-violet-100 dark:bg-violet-950/20 dark:text-violet-400 dark:border-violet-900/30',
  'bg-purple-50 text-purple-600 border-purple-100 dark:bg-purple-950/20 dark:text-purple-400 dark:border-purple-900/30',
  'bg-fuchsia-50 text-fuchsia-600 border-fuchsia-100 dark:bg-fuchsia-950/20 dark:text-fuchsia-400 dark:border-fuchsia-900/30',
  'bg-rose-50 text-rose-600 border-rose-100 dark:bg-rose-950/20 dark:text-rose-400 dark:border-rose-900/30'
];

const getUserColor = (name) => {
  if (!name || typeof name !== "string") return USER_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % USER_COLORS.length;
  return USER_COLORS[index];
};

const CrownIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M5 16L3 5L8.5 10L12 4L15.5 10L21 5L19 16H5ZM19 19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V18H19V19Z" />
  </svg>
);

const getLeadFieldValue = (lead, label) => {
  if (!lead?.LeadFieldCategories?.length) return null;

  const normalizedLabel = label.toLowerCase();
  const allFields = lead.LeadFieldCategories.flatMap((category) => category.Fields || []);

  const field = allFields.find((fieldItem) => {
    const fieldLabel = (fieldItem.FieldLabel || fieldItem.FieldName || "").toLowerCase();
    return fieldLabel.includes(normalizedLabel) || (fieldItem.FieldName || "").toLowerCase().includes(normalizedLabel);
  });

  return field?.FieldValue || field?.FieldValueText || field?.FieldValueLabel || null;
};


export default function LeadRightDrawerContent({ user, onClose, onViewDetails, hideTabs = false, onRefresh, onDataFetched, initialLeadDetails = null, isParentFetching = false, entityName, initialTab, onViewContactsTab, useDocumentsData: useDocumentsDataProp }) {

  const [activeTab, setActiveTab] = useState(initialTab || (hideTabs ? "details" : "notes"));
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [isEmailMessageModal, setIsEmailMessageModal] = useState(false);
  const [isSmsMessageModal, setIsSmsMessageModal] = useState(false);
  const [isWhatsAppMessageModal, setIsWhatsAppMessageModal] = useState(false);
  const [isFollowUpScheduleModal, setIsFollowUpScheduleModal] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [isImportantForNewNote, setIsImportantForNewNote] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isVerifying, setIsVerifying] = useState({ phone: false, email: false });
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // ✔️ Initialize from prop if provided — skips fetch entirely
  const [leadDetails, setLeadDetails] = useState(initialLeadDetails || null);
  const [isLeadDetailsLoading, setIsLeadDetailsLoading] = useState(false);
  const [leadDetailsError, setLeadDetailsError] = useState(null);
  const [showQuickLeadModal, setShowQuickLeadModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [pendingJobCancelStageData, setPendingJobCancelStageData] = useState(null);
  const [isJobCancelStageChangeSubmitting, setIsJobCancelStageChangeSubmitting] = useState(false);
  const [showConvertToJobModal, setShowConvertToJobModal] = useState(false);
  const [showReactivateJobModal, setShowReactivateJobModal] = useState(false);
  const [reactivateJobId, setReactivateJobId] = useState(null);
  const [reactivateCurrentStatusId, setReactivateCurrentStatusId] = useState(null);
  const [pendingRevertLeadStageData, setPendingRevertLeadStageData] = useState(null);
  const [isRevertingStage, setIsRevertingStage] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [showInteractionLevelModal, setShowInteractionLevelModal] = useState(false);
  const [selectedFormForApplication, setSelectedFormForApplication] = useState(null);
  const [isComingFromPaymentFlow, setIsComingFromPaymentFlow] = useState(false);
  const [isAssignOwnerModalOpen, setIsAssignOwnerModalOpen] = useState(false);

  const [selectedActivityType, setSelectedActivityType] = useState(null); // for dropdown pre-selection

  // Fetch activity types for the Activity dropdown
  const { types: activityTypes, isLoading: loadingActivityTypes } = useActivityTypes();

  // States for Tag Updates
  const [isTagsUpdating, setIsTagsUpdating] = useState(false);

  const [detailsSubTab, setDetailsSubTab] = useState("personal");
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedOwners, setSelectedOwners] = useState([]);
  const [isAssignSubmitting, setIsAssignSubmitting] = useState(false);

  const {
    localInteractionLevel, localPriority, localStage, localSubStage, localStageConstant, localSubStageConstant,
    setLocalInteractionLevel, setLocalPriority, setLocalStage, setLocalSubStage, setLocalStageConstant, setLocalSubStageConstant
  } = useLeadOptimisticState();

  const isMobileView = useMediaQuery("(max-width: 767px)");

  // State for managing Manage Tags Popover
  const [isManageTagsOpen, setIsManageTagsOpen] = useState(false);
  const manageTagsPopoverRef = React.useRef(null);
  const tagSelectorRef = React.useRef(null);

  // Close manage tags popover when clicking outside, but exclude tag selector elements and modal dialogs (e.g. ConfirmModal)
  useClickOutside(manageTagsPopoverRef, (event) => {
    if (isManageTagsOpen) {
      // Don't close if clicking within the tag selector container (includes combobox dropdown)
      if (tagSelectorRef.current && tagSelectorRef.current.contains(event.target)) {
        return;
      }

      // Also check for clicks on elements that might be portaled outside (like combobox or dialog modals)
      const target = event.target;
      const isClickOnCombobox =
        target.closest('[role="option"]') ||
        target.closest('[role="combobox"]') ||
        target.closest('[role="listbox"]');

      const isClickOnModal =
        target.closest('[role="dialog"]') ||
        target.closest('[id*="headlessui-dialog"]') ||
        target.closest('.z-\\[10000\\]');

      if (!isClickOnCombobox && !isClickOnModal) {
        setIsManageTagsOpen(false);
      }
    }
  }, 'click');

  // ✅ The lead data is either the fetched details or the initial user row data
  // ✅ In hideTabs mode (inline details), prefer the 'user' prop from parent state
  // In drawer mode, prefer the fetched 'leadDetails'
  const baseLead = (hideTabs && user) ? user : (leadDetails || user);
  // Apply optimistic overrides so all child tabs (like LeadDetailsForm) see the instant updates
  const lead = baseLead ? {
    ...baseLead,
    ...(localInteractionLevel ? { InteractionLevelName: localInteractionLevel, LeadStatus: localInteractionLevel } : {}),
    ...(localPriority ? { Priority: localPriority, priority: localPriority } : {}),
    ...(localStage ? { stage: localStage, StageName: localStage } : {}),
    ...(localSubStage ? { subStage: localSubStage, SubStageName: localSubStage } : {})
  } : null;

  const leadId = lead?.id || lead?.LeadId || lead?.leadId;
  const { contacts: leadContactsList } = useContacts(leadId || false);

  // When a shared useDocumentsData is passed from the parent (details page),
  // we pass null to the internal hook so it skips all fetching. This prevents
  // double API calls for the same leadId.
  const internalDocumentsData = useDocuments(useDocumentsDataProp ? null : leadId);
  const useDocumentsData = useDocumentsDataProp || internalDocumentsData;
  const { totalRecords: apiDocumentsCount } = useDocumentsData;

  const documentsCount = apiDocumentsCount || 0;

  // ✅ Stable ref for onDataFetched — prevents useCallback from recreating when prop changes
  const onDataFetchedRef = React.useRef(onDataFetched);
  React.useEffect(() => { onDataFetchedRef.current = onDataFetched; }, [onDataFetched]);

  // ✅ Stable ref for initialLeadDetails — prevents fetchLeadDetails from recreating every
  // time onDataFetched fires (which sets fullLeadDetails → initialLeadDetails changes → loop)
  const initialLeadDetailsRef = React.useRef(initialLeadDetails);
  React.useEffect(() => { initialLeadDetailsRef.current = initialLeadDetails; }, [initialLeadDetails]);

  // ✅ Deduplication: track the last lead ID we fetched to avoid repeated API calls
  const lastFetchedLeadId = React.useRef(
    initialLeadDetails
      ? (initialLeadDetails?.id || initialLeadDetails?.LeadId || initialLeadDetails?.leadId || null)
      : null
  );
  const isFetchingRef = React.useRef(false);

  // ✅ Track visited tabs for lazy persistent mounting

  const [visitedTabs, setVisitedTabs] = useState(new Set([activeTab || initialTab || "notes"]));


  useEffect(() => {
    if (activeTab) {
      setVisitedTabs(prev => new Set([...prev, activeTab]));
    }
  }, [activeTab]);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const fetchLeadDetails = useCallback(async (forceRefresh = false, silent = false) => {
    if (!user?.id && !user?.LeadId && !user?.leadId) return;

    const leadId = user?.id || user?.LeadId || user?.leadId;

    // ✅ Skip if parent is already fetching (important for mobile prefetch)
    if (isParentFetching && !forceRefresh) return;

    // ✅ Optimization for Mobile: Skip fetch if we are hidden (not in summary mode) 
    // and this is just the mount-fetch. DetailsView handles its own fetch.
    const isMobile = window.innerWidth < 768;
    if (isMobile && hideTabs && !forceRefresh && !initialLeadDetailsRef.current) return;

    // ✅ Skip if we already fetched this exact lead (unless explicitly refreshed)
    // if (!forceRefresh && String(lastFetchedLeadId.current) === String(leadId)) return;
    // ✅ Skip if a fetch is already in progress
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    lastFetchedLeadId.current = leadId;
    if (!silent) setIsLeadDetailsLoading(true);
    setLeadDetailsError(null);

    try {
      if (silent) {
        // Give the backend a moment to update before we refetch
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      const result = await getLead(leadId, true);
      if (result.success && result.data?.data) {
        const transformedData = transformLeadDetailsApiData(result.data.data, leadId);
        setLeadDetails(transformedData);
        if (onDataFetchedRef.current) onDataFetchedRef.current(transformedData);
      } else {
        setLeadDetails(null);

        setLeadDetailsError(result.error || "Failed to load lead details");
      }
    } catch (err) {
      console.error("Lead summary fetch error:", err);
      setLeadDetails(null);

      setLeadDetailsError(err.message || "Failed to load lead details");
    } finally {
      setIsLeadDetailsLoading(false);
      isFetchingRef.current = false;
    }
  }, [user?.id, user?.LeadId, user?.leadId, isParentFetching, hideTabs]);

  const handleTagsChange = async (newTagIds) => {
    const leadId = lead?.id || lead?.LeadId || lead?.leadId;
    if (!leadId) return;

    setIsTagsUpdating(true);
    try {
      const response = await upsertLeadTags({
        leadId: leadId,
        tags: newTagIds
      });

      if (response.success) {
        // Refetch lead details to get updated tags with metadata
        fetchLeadDetails(true, true);
        if (onRefresh) onRefresh();
      } else {
        toast.error(response.error || "Failed to update tags");
      }
    } catch (error) {
      console.error("Error updating tags:", error);
      toast.error("An error occurred while updating tags");
    } finally {
      setIsTagsUpdating(false);
    }
  };

  // ✅ Sync with initialLeadDetails changes
  useEffect(() => {
    if (initialLeadDetails && !leadDetails) {
      setLeadDetails(initialLeadDetails);
      lastFetchedLeadId.current = initialLeadDetails?.id || initialLeadDetails?.LeadId || initialLeadDetails?.leadId;
    }
  }, [initialLeadDetails, leadDetails]);

  useEffect(() => {
    fetchLeadDetails();
  }, [fetchLeadDetails]);

  // When user explicitly refreshes, force a re-fetch even for the same lead ID
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchLeadDetails(true, true);
    }
  }, [refreshTrigger]);

  // ✅ Reset optimistic state and clear stale details when switching leads
  useEffect(() => {
    const leadId = user?.id || user?.LeadId || user?.leadId;
    if (leadId) {
      // Clear optimistic overrides
      setLocalStage(null);
      setLocalSubStage(null);
      setLocalPriority(null);
      setLocalInteractionLevel(null);

      // Clear stale detailed data so we fallback to 'user' prop (summary data) until fetch completes
      setLeadDetails(null);
    }
  }, [user?.id, user?.LeadId, user?.leadId, setLocalStage, setLocalSubStage, setLocalPriority, setLocalInteractionLevel]);

  // Fetch users when detailsSubTab becomes "assign" or when lead changes
  useEffect(() => {
    if (detailsSubTab === "assign") {
      const fetchUsers = async () => {
        setIsLoadingUsers(true);
        try {
          const response = await getInternalUsersForAssigningLeads();
          if (response.success) {
            setUsers(response.data || []);
          } else {
            setUsers([]);
          }
        } catch (error) {
          console.error("Error fetching users:", error);
          setUsers([]);
        } finally {
          setIsLoadingUsers(false);
        }
      };
      fetchUsers();
    }
  }, [detailsSubTab]);

  // Seed selectedOwners when lead owners change or when detailsSubTab becomes "assign"
  useEffect(() => {
    if (lead?.Owners) {
      setSelectedOwners(
        lead.Owners.map((o) => ({
          ownerId: String(o.OwnerId || ""),
          isPrimary: !!(o.isPrimary || o.IsPrimary),
        }))
      );
    } else {
      setSelectedOwners([]);
    }
  }, [lead?.Owners, detailsSubTab]);

  const handleAssignSave = async () => {
    if (!selectedOwners.length) {
      toast.error("Please select at least one user");
      return;
    }
    setIsAssignSubmitting(true);
    try {
      const ownersPayload = selectedOwners.map((o) => ({
        OwnerId: o.ownerId,
        IsPrimary: o.isPrimary,
      }));
      const result = await updateLeadOwner(lead?.id || lead?.LeadId || lead?.leadId, ownersPayload);
      if (result.success) {
        toast.success(result.message || "Lead user assigned successfully");
        invalidateTimelineCache(lead?.id || lead?.LeadId || lead?.leadId);
        if (onRefresh) onRefresh();
        fetchLeadDetails(true, true);
      } else {
        toast.error(result.error || "Failed to assign user");
      }
    } catch (error) {
      console.error("Error assigning user:", error);
      toast.error("Failed to assign user");
    } finally {
      setIsAssignSubmitting(false);
    }
  };


  const handleVerifyPhoneEmail = async (isPhone, close) => {
    const fieldType = isPhone ? "phone" : "email";
    const fieldValue = isPhone ? phoneNumber : emailAddress;

    if (!fieldValue || fieldValue === "N/A") {
      toast.error(`No ${fieldType} found to verify`);
      close();
      return;
    }

    setIsVerifying({ ...isVerifying, [fieldType]: true });

    try {
      const payload = {
        leadId: lead?.id || lead?.LeadId,
        fieldValue: fieldValue,
        isPhone: isPhone,
        isEmail: !isPhone
      };

      const result = await counselorVerifyPhoneEmail(payload);

      if (result.success) {
        toast.success(result.message || `${isPhone ? 'Phone' : 'Email'} verified successfully`);
        if (onRefresh) onRefresh();
        // Also refresh local data forcefully and silently
        fetchLeadDetails(true, true);
      } else {
        toast.error(result.message || `Failed to verify ${fieldType}`);
      }
    } catch (error) {
      console.error(`Error verifying ${fieldType}:`, error);
      toast.error(`Failed to verify ${fieldType}`);
    } finally {
      setIsVerifying({ ...isVerifying, [fieldType]: false });
      close();
    }
  };


  const displayLeadId = lead?.displayId || lead?.DisplayLeadId || lead?.id || lead?.LeadId || lead?.leadId;
  const phoneNumber = formatNumber(lead?.PhoneNumber) || "N/A";
  const emailAddress = lead?.EmailId || lead?.email || getLeadFieldValue(lead, "email") || "N/A";
  const stageName = lead?.stage || lead?.StageName || "N/A";
  const substageName = lead?.subStage || lead?.SubStageName || lead?.substage || lead?.SubstageName || "N/A";
  const stageConstant = lead?.stageConstant || "N/A";
  const subStageConstant = lead?.subStageConstant || "N/A";

  // Enforce read-only status for Applicants and Applications
  const isReadOnly = entityName === "Applicant" ||
    entityName === "Application" ||
    stageConstant?.toLowerCase() === "applicant" ||
    stageConstant?.toLowerCase() === "application" ||
    stageConstant?.toLowerCase() === "win" ||
    stageConstant?.toLowerCase() === "closed";

  const getLeadScoreColor = (score) => {
    if (!score || score === "Unscored") return "neutral";
    const priority = calculatePriority(score);
    switch (priority?.id) {
      case "hot":
      case "urgent":
        return "error";
      case "high":
        return "error";
      case "medium":
        return "warning";
      case "low":
        return "success";
      default:
        return "neutral";
    }
  };

  const getPriorityStyles = (priority) => {
    const p = priority?.toLowerCase();
    if (p === 'high' || p === 'urgent' || p === 'hot') {
      return "bg-[#fef2f2] text-[#ef4444] hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400";
    }
    if (p === 'medium') {
      return "bg-yellow-50 text-yellow-600 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400";
    }
    if (p === 'low') {
      return "bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400";
    }
    return "bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400";
  };

  const handleSubmitNote = async () => {
    if (!newNoteText.trim()) return;

    setIsSubmittingNote(true);
    try {
      const leadId = user?.id || user?.LeadId || user?.leadId;
      const result = await addLeadNote(leadId, newNoteText, isImportantForNewNote);
      if (result.success) {
        toast.success("Note added successfully!");
        setNewNoteText("");
        setIsImportantForNewNote(false);
        setIsAddingNote(false);
        setRefreshTrigger((prev) => prev + 1);
        invalidateTimelineCache(leadId);
        if (onRefresh && typeof onRefresh === "function") {
          onRefresh();
        }
      } else {
        toast.error(result.error || "Failed to add note");
      }
    } catch (error) {
      console.error("Error adding note:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsSubmittingNote(false);
    }
  };



  const handleCancelReactivate = async () => {
    if (pendingRevertLeadStageData) {
      const { leadId, stageId, subStageId } = pendingRevertLeadStageData;
      if (leadId && stageId) {
        setIsRevertingStage(true);
        try {
          const revertRes = await bulkUpdateLeadStage({
            leadIds: [leadId],
            stageId: stageId,
            subStageId: subStageId,
            remarks: "Stage change reverted due to job reactivation cancellation",
          });
          if (revertRes.success) {
            toast.success("Lead restored to its previous stage successfully");
            setPendingRevertLeadStageData(null);
            setShowReactivateJobModal(false);
            fetchLeadDetails(true, true);
            if (onRefresh) onRefresh();
          } else {
            toast.error(revertRes.error || "Failed to revert Lead stage");
          }
        } catch (err) {
          console.error("Error reverting lead stage:", err);
          toast.error("An error occurred while reverting Lead stage");
        } finally {
          setIsRevertingStage(false);
          setShowCancelReactivateConfirm(false);
        }
      } else {
        setPendingRevertLeadStageData(null);
        setShowReactivateJobModal(false);
        setShowCancelReactivateConfirm(false);
      }
    } else {
      setShowReactivateJobModal(false);
      setShowCancelReactivateConfirm(false);
    }
  };

  const handlestageUpdate = async (stageData) => {
    const prevStageId = leadDetails?.StageId || leadDetails?.stageId || lead?.StageId || lead?.stageId || null;
    const prevSubStageId = leadDetails?.SubStageId || leadDetails?.subStageId || lead?.SubStageId || lead?.subStageId || null;
    try {
      const result = await bulkUpdateLeadStage({
        leadIds: [lead?.id || lead?.LeadId || lead?.leadId],
        stageId: stageData.stageId,
        subStageId: stageData.subStageId,
        remarks: stageData.notes || "",
        leadScore: stageData.leadScore,
        fieldValues: stageData.fieldValues,
      });

      if (result.success) {
        // Check for formId in response data to open application form automatically
        if (result.data) {
          try {
            const parsedData = typeof result.data === 'string' ? JSON.parse(result.data) : result.data;
            if (parsedData.formId) {
              setSelectedFormForApplication({ FormId: parsedData.formId });

              // Delay opening to allow confirmation modal flow to complete
              setTimeout(() => {
                setShowQuickLeadModal(true);
              }, 500);
            }
          } catch (error) {
            console.error("Error parsing formId from response:", error);
          }
        }
        setLocalStage(stageData.leadStage);
        setLocalSubStage(stageData.subStage);

        // Show Convert to Job modal when target stage is eligible and lead is not converted
        if (shouldTriggerJobConversion(stageData, leadDetails || lead)) {
          setTimeout(() => setShowConvertToJobModal(true), 600);
        }

        // Offer to reactivate cancelled job if lead is already converted and target stage is a job stage
        if (shouldOfferJobReactivation(stageData, leadDetails || lead)) {
          const leadId = leadDetails?.id || leadDetails?.LeadId || leadDetails?.leadId || lead?.id || lead?.LeadId || lead?.leadId;
          if (leadId) {
            getJobByLeadId(leadId).then((res) => {
              if (res.success && res.data) {
                let jobData = res.data?.data || res.data;
                if (typeof jobData === "string") {
                  try { jobData = JSON.parse(jobData); } catch { }
                }
                if (Array.isArray(jobData) && jobData.length > 0) jobData = jobData[0];
                const jobObj = jobData?.Job || jobData;
                const statusName = (jobObj?.StatusName || "").toLowerCase();
                const statusId = jobObj?.StatusId;
                const jobId = jobObj?.JobId;
                if (jobId && statusName === "cancelled") {
                  setReactivateJobId(jobId);
                  setReactivateCurrentStatusId(statusId);
                  setPendingRevertLeadStageData({
                    leadId,
                    stageId: prevStageId,
                    subStageId: prevSubStageId,
                  });
                  setTimeout(() => setShowReactivateJobModal(true), 600);
                }
              }
            });
          }
        }

        // Optimistic update
        setLeadDetails(prev => prev ? ({
          ...prev,
          stage: stageData.leadStage,
          subStage: stageData.subStage
        }) : prev);
        toast.success("Stage updated successfully!");
        setRefreshTrigger((prev) => prev + 1);
        invalidateTimelineCache(lead?.id || lead?.LeadId || lead?.leadId);
        invalidateNotesCache(lead?.id || lead?.LeadId || lead?.leadId);
        if (onRefresh) onRefresh();
        return true;
      } else {
        toast.error(result.error || "Failed to update stage");
        return false;
      }
    } catch (error) {
      console.error("Error updated stage:", error);
      toast.error("An error occurred while updated stage");
      return false;
    }
  };

  // Intercepts StageChangeModal's submit: if the lead is already converted
  // to a job and the target stage would take it off the job's 3 stages,
  // hold off and warn the user first (cancelling the job is a side effect
  // of confirming), instead of applying the stage change immediately.
  const handleStageChangeRequest = async (stageData) => {
    if (shouldWarnJobCancellation(stageData, leadDetails || lead)) {
      setPendingJobCancelStageData(stageData);
      return false; // keep StageChangeModal open until the warning is resolved
    }
    return handlestageUpdate(stageData);
  };

  const confirmJobCancellingStageChange = async () => {
    if (!pendingJobCancelStageData) return;
    setIsJobCancelStageChangeSubmitting(true);
    const result = await handlestageUpdate(pendingJobCancelStageData);
    setIsJobCancelStageChangeSubmitting(false);
    setPendingJobCancelStageData(null);
    if (result !== false) {
      setShowStageModal(false);
    }
  };

  /*
  // Handle Access Denied / API Errors
  if (leadDetailsError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-white dark:bg-dark-900">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 mb-4">
          <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
        </div>
        <h3 className="text-[16px] font-bold text-gray-900 dark:text-dark-100">Access Restricted</h3>
        <p className="mt-2 text-[13px] text-gray-500 dark:text-dark-400 max-w-[250px]">
          You are not assigned to this Lead, or you do not have permission to view this data.
        </p>
      </div>
    );
  }
  */

  if (!lead) return null;
  /*
  // 1. PRIORITIZE ERROR: If the API told us "Forbidden" or "Not Found", show it immediately
  if (leadDetailsError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-white dark:bg-dark-900">
        <div className="flex items-center justify-center w-20 h-20 rounded-full bg-red-50 dark:bg-red-900/20 mb-6">
          <ExclamationTriangleIcon className="w-10 h-10 text-red-500" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-dark-100">Access Restricted</h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-dark-400 max-w-[280px]">
          You are not assigned to this lead or do not have permission to view this data.
        </p>
        <Button variant="outlined" className="mt-6" onClick={onClose}>
          Close Drawer
        </Button>
      </div>
    );
  }
  */

  // 2. LOADING STATE: Only show spinner if there is no error and no data yet
  if (isLeadDetailsLoading && !leadDetails) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-white dark:bg-dark-900">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-500 border-t-transparent"></div>
          <p className="text-xs text-gray-500 animate-pulse">Loading lead info...</p>
        </div>
      </div>
    );
  }

  /*
  // 3. DATA CHECK: If not loading but leadDetails is still empty, it's an implicit error
  if (!isLeadDetailsLoading && !leadDetails) {
     setLeadDetailsError("Lead not found"); // Trigger the error UI above
     return null;
  }
  */

  // 4. SUCCESS: Normal rendering continues...
  const leadTitle = lead?.title || lead?.leadTitle || lead?.LeadRawJsonData?.find((item) => item.MasterFieldName?.toLowerCase() === "title")?.Value;
  const leadName = lead?.FullName || (lead?.name && !["Unknown", "Unknown Lead", "Unknown User", "N/A", "-"].includes(lead.name) ? lead.name : null) || lead?.LeadName || leadTitle || "Lead";
  const ownersList = lead?.Owners;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white dark:bg-dark-900">
      <div className="px-4 sm:px-5 pt-2 sm:pt-2.5 pb-1.5 sm:pb-2 bg-white dark:bg-dark-900 border-b border-gray-100 dark:border-dark-800 shrink-0 relative z-20">

        {/* ── TOP ROW: Name + Score ── */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <h2
            className="text-[14px] sm:text-[15px] font-bold text-gray-900 dark:text-dark-100 leading-tight truncate flex-1 min-w-0"
            title={leadTitle || leadName}
          >
            {leadTitle || leadName}
          </h2>
          {/* Score circle — right-aligned, compact */}
          <div className="flex flex-col items-center shrink-0">
            <Circlebar
              value={lead?.LeadScore === "Unscored" ? 0 : lead?.LeadScore || lead?.leadScore || 0}
              size={9}
              strokeWidth={7}
              color={getLeadScoreColor(lead?.LeadScore || lead?.leadScore)}
              className="mx-auto"
            >
              <div className="text-center">
                <div className="text-[9px] font-bold text-gray-900 dark:text-dark-100 leading-none">
                  {lead?.LeadScore === "Unscored" ? "--" : lead?.LeadScore || lead?.leadScore || 0}
                </div>
              </div>
            </Circlebar>
            <div className="text-[7px] font-medium text-gray-400 dark:text-gray-500 mt-0.5 uppercase tracking-wide">Score</div>
          </div>
        </div>

        {/* ── ROW 2: Status + Priority + Lead ID ── */}
        <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap mb-1.5">
          <div className="flex items-center gap-1">
            <span className="text-[11px] sm:text-xs font-semibold text-gray-500 dark:text-dark-300">Status:</span>
            {/* Interaction Level / Status badge */}
            <span
              onClick={canEditLead() ? () => setShowInteractionLevelModal(true) : undefined}
              className={clsx(
                "inline-flex items-center px-1.5 py-[1.5px] rounded text-[9px] sm:text-[10px] font-bold bg-blue-50 text-blue-700 dark:bg-blue-900/25 dark:text-blue-300 border border-blue-200/60 dark:border-blue-800/40 uppercase tracking-wide transition-colors",
                canEditLead() ? "cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/40" : "cursor-default"
              )}
            >
              {lead?.LeadStatus || lead?.InteractionLevelName || "COLD"}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <span className="text-[11px] sm:text-xs font-semibold text-gray-500 dark:text-dark-300">Priority:</span>
            {/* Priority badge */}
            <span
              onClick={canEditLead() ? () => setShowPriorityModal(true) : undefined}
              className={clsx(
                "inline-flex items-center px-1.5 py-[1.5px] rounded text-[9px] sm:text-[10px] font-bold transition-colors uppercase tracking-wide border",
                canEditLead() ? "cursor-pointer" : "cursor-default",
                (lead?.Priority || "HIGH")?.toLowerCase() === "high" || (lead?.Priority || "HIGH")?.toLowerCase() === "urgent" || (lead?.Priority || "HIGH")?.toLowerCase() === "hot"
                  ? "bg-red-50 text-red-700 border-red-200/60 hover:bg-red-100 dark:bg-red-900/25 dark:text-red-300 dark:border-red-800/40 dark:hover:bg-red-900/40"
                  : (lead?.Priority)?.toLowerCase() === "medium"
                    ? "bg-amber-50 text-amber-700 border-amber-200/60 hover:bg-amber-100 dark:bg-amber-900/25 dark:text-amber-300 dark:border-amber-800/40 dark:hover:bg-amber-900/40"
                    : "bg-emerald-50 text-emerald-700 border-emerald-200/60 hover:bg-emerald-100 dark:bg-emerald-900/25 dark:text-emerald-300 dark:border-emerald-800/40 dark:hover:bg-emerald-900/40"
              )}
            >
              {lead?.Priority || "HIGH"}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <span className="text-[11px] sm:text-xs font-semibold text-gray-500 dark:text-dark-300">Lead ID:</span>
            <span className="text-[11px] sm:text-xs font-medium text-gray-400 dark:text-dark-400">
              #{displayLeadId || "N/A"}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <span className="text-[11px] sm:text-xs font-semibold text-gray-500 dark:text-dark-300">Source:</span>
            <span className="text-[11px] sm:text-xs font-medium text-gray-400 dark:text-dark-400" title={lead?.LeadSourceName || lead?.source}>
              {lead?.LeadSourceFormId
                ? `Form · ${lead?.LeadSourceName || lead?.source || "N/A"}`
                : (lead?.LeadSourceName || lead?.source || "Manual")}
            </span>
          </div>
        </div>

        {/* ── ROW 3: Stage pill + Tags ── */}
        <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap mb-1">
          <div
            onClick={canEditLead() ? () => setShowStageModal(true) : undefined}
            className={clsx(
              "flex items-center gap-1",
              canEditLead() ? "cursor-pointer group/stage" : "cursor-default"
            )}
          >
            <span className="text-[11px] sm:text-xs font-semibold text-gray-500 dark:text-dark-300">Stage:</span>
            {/* Stage + Substage pill */}
            <div
              className={clsx(
                "inline-flex items-center gap-1 px-2 py-[2px] rounded text-[9px] sm:text-[10px] font-bold transition-all shrink-0 uppercase tracking-wide",
                canEditLead()
                  ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200/60 dark:border-primary-800/40 group-hover/stage:bg-primary-100 dark:group-hover/stage:bg-primary-900/40 active:scale-95"
                  : "bg-gray-50 dark:bg-dark-900/40 text-gray-500 dark:text-dark-400 border border-gray-200 dark:border-dark-700 opacity-75"
              )}
            >
              <span>{stageName === "N/A" ? "NEW" : stageName}</span>
              {substageName && substageName !== "N/A" && (
                <>
                  <span className="opacity-40">›</span>
                  <span className="opacity-80">{substageName}</span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1">
            <span className="text-[11px] sm:text-xs font-semibold text-gray-500 dark:text-dark-300">Tags:</span>
            {/* Tags area */}
            <div className="flex items-center gap-1 min-w-0 relative" ref={manageTagsPopoverRef}>
              <button
                type="button"
                disabled={!canEditLead()}
                className={clsx(
                  "flex items-center gap-1 min-w-0 outline-none border-0 bg-transparent p-0",
                  canEditLead() ? "cursor-pointer" : "cursor-default"
                )}
                onClick={() => setIsManageTagsOpen((prev) => !prev)}
              >
                <div className="flex items-center gap-1 overflow-hidden">
                  {lead?.tags?.length > 0 ? (
                    <>
                      {lead.tags.slice(0, 2).map((tag, idx) => {
                        const color = tag.ColorCode || '#6b7280';
                        return (
                          <span
                            key={tag.TagId || tag.Id || idx}
                            className={clsx(
                              "inline-flex items-center px-1.5 py-[1.5px] rounded text-[9px] sm:text-[10px] font-bold transition-all border border-transparent shrink-0 uppercase tracking-wide",
                              canEditLead() ? "cursor-pointer hover:border-current" : "cursor-default"
                            )}
                            style={{ backgroundColor: `${color}18`, color }}
                          >
                            {tag.TagName || tag.tagName}
                          </span>
                        );
                      })}
                      {lead.tags.length > 2 && (
                        <span className={clsx("inline-flex items-center px-1.5 py-[1.5px] rounded text-[9px] font-bold bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-dark-400 transition-colors shrink-0", canEditLead() ? "cursor-pointer hover:bg-gray-200 dark:hover:bg-dark-600" : "cursor-default")}>
                          +{lead.tags.length - 2}
                        </span>
                      )}
                    </>
                  ) : canEditLead() ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-[1.5px] rounded text-[9px] font-bold bg-gray-50 dark:bg-dark-800 text-gray-400 dark:text-dark-500 border border-dashed border-gray-200 dark:border-dark-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors">
                      + Tags
                    </span>
                  ) : (
                    <span className="text-[11px] text-gray-400">None</span>
                  )}
                </div>
              </button>

              <Transition
                as={Fragment}
                show={isManageTagsOpen}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <div
                  className="absolute top-full left-[-130px] sm:left-[-130px] z-[50] w-[270px] sm:w-[270px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-150px)] mt-2 rounded-2xl bg-white dark:bg-dark-700 shadow-[0_20px_60px_rgba(0,0,0,0.2)] border border-gray-100 dark:border-dark-600 overflow-visible"
                >
                  <div className="flex flex-col overflow-visible">
                    <div className="flex justify-between items-center bg-gray-50/50 dark:bg-dark-800/50 px-4 py-3 border-b border-gray-100 dark:border-dark-600 rounded-t-2xl">
                      <span className="text-[11px] font-black uppercase tracking-[0.1em] text-gray-500">Manage Tags</span>
                      <button
                        type="button"
                        onClick={() => setIsManageTagsOpen(false)}
                        className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-600 text-gray-400 transition-colors"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="p-4 overflow-visible" ref={tagSelectorRef}>
                      <LeadTagSelector
                        value={(lead?.Tags || lead?.tags || []).map(t => t.TagId || t.Id || t)}
                        onChange={(newIds) => {
                          handleTagsChange(newIds);
                        }}
                        className="!space-y-0"
                        defaultOpen={true}
                        inline={true}
                      />
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </div>

        {/* ── ROW 3.5: Stage Fields capsules ── */}
        {lead?.StageFields && lead.StageFields.length > 0 && (
          <div className="flex items-center gap-x-2 gap-y-1 flex-wrap mb-1.5 mt-0.5">
            <span className="text-[11px] sm:text-xs font-semibold text-gray-500 dark:text-dark-300">Stage Details:</span>
            {lead.StageFields.slice(0, 2).map((field, idx) => {
              const displayVal = (isCostOrRevenueField(field) && field.FieldValue)
                ? `$${formatCostAustralian(field.FieldValue)}`
                : field.FieldValue;
              return (
                <span
                  key={field.StageFieldId || idx}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-300 border border-amber-200/50 dark:border-amber-900/30 tracking-wide max-w-[250px] min-w-0"
                  title={`${field.FieldLabel || field.FieldName} - ${displayVal}`}
                >
                  <span className="shrink-0 mr-1 font-semibold">
                    {field.FieldLabel || field.FieldName} -
                  </span>
                  <span className="truncate font-medium flex-1">
                    {displayVal}
                  </span>
                </span>
              );
            })}
            {lead.StageFields.length > 2 && (
              <span className="inline-flex items-center px-1.5 py-[1.5px] rounded-full text-[9px] font-bold bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-dark-400 border border-gray-200 dark:border-dark-600">
                +{lead.StageFields.length - 2}
              </span>
            )}
          </div>
        )}

        {/* ── ROW 4: Assigned Users ── */}
        <div className="flex items-center">
          {ownersList && ownersList.length > 0 ? (
            <div
              onClick={canAssignLead() ? () => setIsAssignOwnerModalOpen(true) : undefined}
              className={clsx(
                "inline-flex items-center gap-1.5 px-2 py-[2px] rounded bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-900/30 transition-all",
                canAssignLead() ? "cursor-pointer hover:bg-amber-100/60 dark:hover:bg-amber-900/40 active:scale-95" : "cursor-default"
              )}
            >
              <span className="text-[9px] sm:text-[10px] font-medium text-amber-700 dark:text-amber-400 whitespace-nowrap">Assigned to:</span>
              <div className="flex items-center -space-x-1">
                {ownersList.map((owner, idx) => {
                  const name = owner.OwnerName || owner.name || owner.FullName || "Unknown";
                  const initials = getInitials(name);
                  const isPrimary = !!(owner.isPrimary || owner.IsPrimary);
                  return (
                    <div key={idx} className="relative">
                      <div
                        title={name}
                        className={clsx(
                          "flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 rounded-full text-[8px] sm:text-[9px] font-bold border border-white dark:border-dark-900",
                          getUserColor(name)
                        )}
                      >
                        {initials}
                      </div>
                      {isPrimary && (
                        <div className="absolute -top-[6px] left-1/2 -translate-x-1/2 z-10 pointer-events-none">
                          <CrownIcon className="h-2 w-2 sm:h-2.5 sm:w-2.5 text-amber-400 fill-amber-400 drop-shadow" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Show first owner name if only one */}
              {ownersList.length === 1 && (
                <span className="text-[9px] sm:text-[10px] font-semibold text-amber-800 dark:text-amber-300">
                  {ownersList[0]?.OwnerName || ownersList[0]?.name || ownersList[0]?.FullName}
                </span>
              )}
            </div>
          ) : (
            <div
              onClick={canAssignLead() ? () => setIsAssignOwnerModalOpen(true) : undefined}
              className={clsx(
                "inline-flex items-center gap-1 px-2 py-[2px] rounded bg-gray-50 dark:bg-dark-900/40 border border-dashed border-gray-300 dark:border-dark-600 transition-all",
                canAssignLead() ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-800 active:scale-95" : "cursor-default"
              )}
            >
              <UserGroupIcon className="w-3 h-3 text-gray-400 dark:text-dark-500" />
              <span className="text-[9px] sm:text-[10px] font-medium text-gray-500 dark:text-dark-400">Assign Owner</span>
            </div>
          )}
        </div>

        <LeadDrawerContacts
          leadId={leadId}
          leadData={lead}
          onRefresh={onRefresh}
          onViewContactsTab={onViewContactsTab || (() => setActiveTab("contacts"))}
          hideTabs={hideTabs}
        />
      </div>

      <div className="bg-white dark:bg-dark-900 border-b border-gray-200 dark:border-dark-800 shrink-0">
        <div className={`flex ${hideTabs ? "gap-1 px-2" : "gap-1.5 sm:gap-2 px-3 sm:px-5 overflow-x-auto scrollbar-hide"} py-1 w-full`}>
          {/* <button onClick={() => setIsWhatsAppMessageModal(true)} className={`${hideTabs ? "flex-1 min-w-0 px-1" : "flex-none basis-1/4 sm:flex-1 min-w-[70px]"} flex flex-col items-center justify-center gap-1 py-1.5 sm:py-[10px] rounded-lg border border-gray-200 dark:border-dark-800 hover:border-emerald-200 dark:hover:border-emerald-800 hover:bg-emerald-50/30 dark:hover:bg-emerald-900/10 transition-all`}>
            <div className="flex items-center justify-center text-[#10b981] bg-[#ecfdf5] dark:bg-emerald-900/20 rounded-full w-[15px] h-[15px]">
              <ChatBubbleLeftRightIcon className="w-[14px] h-[14px]" />
            </div>
            <span className="text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-dark-200 tracking-tight sm:tracking-wide">WhatsApp</span>
          </button> */}

          {/* {canCall() && (
            <button
              onClick={() => {
                if (!leadContactsList || leadContactsList.length === 0) {
                  toast.error("Please add a contact first");
                } else {
                  setIsCallModalOpen(true);
                }
              }}
              className={`${hideTabs ? "flex-1 min-w-0 px-1" : "flex-none basis-1/4 sm:flex-1 min-w-[60px]"} flex flex-col items-center justify-center gap-0.5 py-1.5 sm:py-2 rounded-lg border border-gray-200 dark:border-dark-800 hover:border-blue-200 dark:hover:border-blue-800 hover:bg-blue-50/30 dark:hover:bg-blue-900/10 transition-all`}
            >
              <div className="flex items-center justify-center text-[#3b82f6] bg-[#eff6ff] dark:bg-blue-900/20 rounded-full w-[18px] h-[18px]">
                <PhoneIcon className="w-[11px] h-[11px]" />
              </div>
              <span className="text-[9px] sm:text-[10px] font-medium text-gray-500 dark:text-dark-200">Call</span>
            </button>
          )} */}

          {canSendEmail() && (
            <button onClick={() => setIsEmailMessageModal(true)} className={`${hideTabs ? "flex-1 min-w-0 px-1" : "flex-none basis-1/4 sm:flex-1 min-w-[60px]"} flex flex-col items-center justify-center gap-0.5 py-1.5 sm:py-2 rounded-lg border border-gray-200 dark:border-dark-800 hover:border-orange-200 dark:hover:border-orange-800 hover:bg-orange-50/30 dark:hover:bg-orange-900/10 transition-all`}>
              <div className="flex items-center justify-center text-[#d97706] bg-[#fef3c7] dark:bg-orange-900/20 rounded-full w-[18px] h-[18px]">
                <EnvelopeIcon className="w-[11px] h-[11px]" />
              </div>
              <span className="text-[9px] sm:text-[10px] font-medium text-gray-500 dark:text-dark-200">Email</span>
            </button>
          )}

          {/* Activity Button — dropdown with activity types */}
          {canCreateFollowUp() && (
            <Popover className="relative flex-none basis-1/4 sm:flex-1 min-w-[60px]">
              {({ open, close }) => (
                <>
                  <PopoverButton
                    className={`w-full flex flex-col items-center justify-center gap-0.5 py-1.5 sm:py-2 rounded-lg border border-gray-200 dark:border-dark-800 hover:border-pink-200 dark:hover:border-pink-800 hover:bg-pink-50/30 dark:hover:bg-pink-900/10 transition-all outline-none ${open ? 'border-pink-300 bg-pink-50/40 dark:border-pink-700 dark:bg-pink-900/20' : ''}`}
                  >
                    <div className="flex items-center justify-center text-[#db2777] bg-[#fce7f3] dark:bg-pink-900/20 rounded-full w-[18px] h-[18px]">
                      <ClockIcon className="w-[11px] h-[11px]" />
                    </div>
                    <span className="text-[9px] sm:text-[10px] font-medium text-gray-500 dark:text-dark-200">Activity</span>
                  </PopoverButton>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-150"
                    enterFrom="opacity-0 translate-y-1 scale-95"
                    enterTo="opacity-100 translate-y-0 scale-100"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100 translate-y-0 scale-100"
                    leaveTo="opacity-0 translate-y-1 scale-95"
                  >
                    <PopoverPanel
                      anchor={{ to: "bottom", gap: 6 }}
                      className="z-[200] min-w-[140px] rounded-xl bg-white dark:bg-dark-800 shadow-[0_20px_50px_rgba(0,0,0,0.18)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.5)] ring-1 ring-black/10 border border-gray-200/60 dark:border-gray-700/50 overflow-hidden"
                    >
                      <div className="py-1">
                        {loadingActivityTypes ? (
                          <div className="px-4 py-3 text-xs text-gray-400 flex items-center gap-2">
                            <div className="h-3 w-3 rounded-full border-2 border-pink-400 border-t-transparent animate-spin" />
                            Loading...
                          </div>
                        ) : activityTypes.length === 0 ? (
                          <div className="px-4 py-3 text-xs text-gray-400">No types available</div>
                        ) : (
                          activityTypes.map((type) => (
                            <button
                              key={type.id ?? type.channelId}
                              onClick={() => {
                                setSelectedActivityType(type);
                                setIsFollowUpScheduleModal(true);
                                close();
                              }}
                              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-gray-700 dark:text-dark-200 hover:bg-pink-50 dark:hover:bg-pink-900/20 hover:text-pink-700 dark:hover:text-pink-300 transition-colors"
                            >
                              <span className="flex-1 text-[12px] font-medium">{type.label}</span>
                            </button>
                          ))
                        )}
                      </div>
                    </PopoverPanel>
                  </Transition>
                </>
              )}
            </Popover>
          )}

          {canSendSMS() && (
            <button
              onClick={() => {
                if (!leadContactsList || leadContactsList.length === 0) {
                  toast.error("Please add a contact first");
                } else {
                  setIsSmsMessageModal(true);
                }
              }}
              className={`${hideTabs ? "flex-1 min-w-0 px-1" : "flex-none basis-1/4 sm:flex-1 min-w-[60px]"} flex flex-col items-center justify-center gap-0.5 py-1.5 sm:py-2 rounded-lg border border-gray-200 dark:border-dark-800 hover:border-gray-300 dark:hover:border-dark-600 hover:bg-gray-50/30 dark:hover:bg-dark-800 transition-all`}
            >
              <div className="flex items-center justify-center text-gray-500 dark:text-dark-400 bg-white dark:bg-dark-800 border border-gray-400 dark:border-dark-600 rounded-full w-[18px] h-[18px]">
                <DevicePhoneMobileIcon className="w-[11px] h-[11px]" />
              </div>
              <span className="text-[9px] sm:text-[10px] font-medium text-gray-500 dark:text-dark-200">SMS</span>
            </button>
          )}
        </div>
      </div>

      {
        !hideTabs && (
          <div className="bg-white dark:bg-dark-900 border-b border-gray-200 dark:border-dark-800 shrink-0">
            <div className="flex gap-4 px-4 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setActiveTab("notes")}
                className={`py-2 text-[11px] sm:text-[12px] font-medium transition-all duration-200 border-b-[2px] whitespace-nowrap ${activeTab === "notes"
                  ? "border-primary-600 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                  }`}
              >
                Notes
              </button>
              <button
                onClick={() => setActiveTab("client-notes")}
                className={`py-2 text-[11px] sm:text-[12px] font-medium transition-all duration-200 border-b-[2px] whitespace-nowrap ${activeTab === "client-notes"
                  ? "border-primary-600 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                  }`}
              >
                Client Notes
              </button>
              <button
                onClick={() => setActiveTab("details")}
                className={`py-2 text-[11px] sm:text-[12px] font-medium transition-all duration-200 border-b-[2px] whitespace-nowrap ${activeTab === "details"
                  ? "border-primary-600 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                  }`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab("timeline")}
                className={`py-2 text-[11px] sm:text-[12px] font-medium transition-all duration-200 border-b-[2px] whitespace-nowrap ${activeTab === "timeline"
                  ? "border-primary-600 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                  }`}
              >
                Timeline
              </button>
              <button
                onClick={() => setActiveTab("activity")}
                className={`py-2 text-[11px] sm:text-[12px] font-medium transition-all duration-200 border-b-[2px] whitespace-nowrap ${activeTab === "activity"
                  ? "border-primary-600 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                  }`}
              >
                Activity
              </button>

              {isMobileView && (
                <>
                  <button
                    onClick={() => setActiveTab("management")}
                    className={`py-2 text-[11px] sm:text-[12px] font-medium transition-all duration-200 border-b-[2px] whitespace-nowrap ${activeTab === "management"
                      ? "border-primary-600 text-primary-600 dark:text-primary-400"
                      : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                      }`}
                  >
                    Management
                  </button>


                  <button
                    onClick={() => setActiveTab("communications")}
                    className={`py-2 text-[11px] sm:text-[12px] font-medium transition-all duration-200 border-b-[2px] whitespace-nowrap ${activeTab === "communications"
                      ? "border-primary-600 text-primary-600 dark:text-primary-400"
                      : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                      }`}
                  >
                    Communications
                  </button>
                </>
              )}

              {/* Contacts */}
              <button
                onClick={() => setActiveTab("contacts")}
                className={`py-2 text-[11px] sm:text-[12px] font-medium transition-all duration-200 border-b-[2px] whitespace-nowrap ${activeTab === "contacts"
                  ? "border-primary-600 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                  }`}
              >
                Contacts
              </button>

              {/* Documents */}
              <button
                onClick={() => setActiveTab("documents")}
                className={`py-2 text-[11px] sm:text-[12px] font-medium transition-all duration-200 border-b-[2px] whitespace-nowrap ${activeTab === "documents"
                  ? "border-primary-600 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                  }`}
              >
                Documents
              </button>
            </div>
          </div>
        )
      }

      <div id="drawer-scroll-container" className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar px-4 py-1 bg-white dark:bg-dark-800 flex flex-col pb-24">
        {/* Notes Tab */}
        {visitedTabs.has("notes") && (
          <div className={activeTab === "notes" ? "block flex-1 flex flex-col h-full mt-0" : "hidden"}>

            <style>{`
              .hide-native-add-btn > div > div:first-child > div:first-child {
                  display: none !important;
              }
              .hide-native-add-btn .rounded-xl.bg-gray-50 {
                  display: flex !important;
                  flex-wrap: nowrap !important;
                  align-items: center !important;
                  justify-content: space-between !important;
                  padding: 0.25rem 0.5rem !important;
                  border-radius: 0.5rem !important;
                  gap: 0.25rem !important;
                  margin-bottom: 0.5rem !important;
              }
              .dark .hide-native-add-btn .rounded-xl.bg-gray-50 {
                  background-color: rgba(30, 41, 59, 0.5) !important;
              }
              .hide-native-add-btn .rounded-xl.bg-gray-50 > div:first-child {
                  flex: 1 1 auto !important;
                  display: flex !important;
                  flex-wrap: nowrap !important;
                  justify-content: space-between !important;
                  align-items: center !important;
                  gap: 0.35rem !important;
                  min-width: 0 !important;
              }
              .hide-native-add-btn .rounded-xl.bg-gray-50 label {
                  margin-right: 2px !important;
                  white-space: nowrap !important;
                  font-size: 11px !important;
              }
              .hide-native-add-btn .rounded-xl.bg-gray-50 .text-right.text-sm {
                  font-size: 10px !important;
                  opacity: 0.8 !important;
              }
              .hide-native-add-btn .react-select__control {
                  min-height: 30px !important;
                  height: 30px !important;
                  border-radius: 0.4rem !important;
                  background-color: transparent !important;
              }
              .dark .hide-native-add-btn .react-select__control {
                  border-color: #334155 !important;
              }
              .dark .hide-native-add-btn .react-select__single-value {
                  color: #f1f5f9 !important;
              }
              .dark .hide-native-add-btn .react-select__menu {
                  background-color: #1e293b !important;
                  border-color: #334155 !important;
              }
              .dark .hide-native-add-btn .react-select__option {
                  background-color: transparent !important;
                  color: #cbd5e1 !important;
              }
              .dark .hide-native-add-btn .react-select__option--is-focused {
                  background-color: #334155 !important;
              }
              .hide-native-add-btn .react-select__value-container {
                  padding: 0 8px !important;
                  font-size: 0.75rem !important;
              }
              .hide-native-add-btn .react-select__indicators {
                  height: 30px !important;
              }
              .hide-native-add-btn .bg-gray-50 label,
              .hide-native-add-btn .bg-gray-50 .text-sm {
                  font-size: 0.75rem !important;
              }
              .dark .hide-native-add-btn .bg-gray-50 label {
                  color: #94a3b8 !important;
              }
              .hide-native-add-btn .prose p {
                  font-size: 0.8125rem !important;
                  line-height: 1.2rem !important;
              }
              .hide-native-add-btn .mb-4 { margin-bottom: 0.25rem !important; }
              .hide-native-add-btn .space-y-4 > :not([hidden]) ~ :not([hidden]) { margin-top: 0.4rem !important; }
              .hide-native-add-btn .p-2 { padding: 0.4rem !important; }
              .hide-native-add-btn .p-8, .hide-native-add-btn .p-4 { padding: 1rem !important; }
              .hide-native-add-btn .text-[10px] { font-size: 0.55rem !important; }
              .hide-native-add-btn h3 { font-size: 0.875rem !important; margin-bottom: 0.25rem !important; }
              .hide-native-add-btn .text-center p { font-size: 0.75rem !important; margin-bottom: 0.5rem !important; }
              .hide-native-add-btn .text-center button { display: none !important; }
              .hide-native-add-btn .overflow-y-auto { 
                  min-height: unset !important;
                  height: auto !important;
                  max-height: 450px !important;
              }
              .hide-native-add-btn .text-center {
                  padding-top: 1rem !important;
                  padding-bottom: 1rem !important;
              }
              .hide-native-add-btn .text-gray-500.text-sm,
              .hide-native-add-btn .text-gray-600.text-sm,
              .hide-native-add-btn .text-right.text-sm {
                  font-size: 0.75rem !important;
                  font-weight: 700 !important;
              }
              .hide-native-add-btn .text-gray-500.text-sm svg,
              .hide-native-add-btn .text-gray-600.text-sm svg {
                  width: 0.75rem !important;
                  height: 0.75rem !important;
              }
              .hide-native-add-btn .rounded-full.border-yellow-200 {
                  padding: 0.15rem 0.4rem !important;
                  font-size: 0.65rem !important;
              }
              .hide-native-add-btn .rounded-full.border-yellow-200 svg {
                  width: 0.7rem !important;
                  height: 0.7rem !important;
              }
              .hide-native-add-btn .prose p {
                  font-size: 0.8125rem !important;
                  line-height: 1.2rem !important;
                  margin-top: 0.5rem !important;
              }
              .dark .hide-native-add-btn .prose p {
                  color: #f1f5f9 !important;
              }
              .scrollbar-hide {
                  scrollbar-width: none !important;
                  -ms-overflow-style: none !important;
              }
              .scrollbar-hide::-webkit-scrollbar {
                  display: none !important;
              }
            `}</style>

            <div className="relative hide-native-add-btn">
              <NotesTab
                leadData={lead}
                isMobile={true}
                openAddModalExternal={isNoteModalOpen}
                setOpenAddModalExternal={setIsNoteModalOpen}
                refreshTrigger={refreshTrigger}
                hideImportantToggle={true}
                onRefreshParent={onRefresh}
                scrollTargetId="drawer-scroll-container"
              />
            </div>
          </div>
        )}

        {/* Details Tab */}
        {visitedTabs.has("details") && (
          <div className={activeTab === "details" ? "block mt-0 space-y-4" : "hidden"}>
            {/* Sub-tabs to switch between Personal Info and Assign Users */}
            <div className="flex border-b border-gray-200 dark:border-dark-700 w-full mb-3 shrink-0">
              <button
                type="button"
                onClick={() => setDetailsSubTab("personal")}
                className={`flex-1 py-2 text-center text-[13px] font-semibold border-b-[2px] transition-all duration-200 whitespace-nowrap ${detailsSubTab === "personal"
                  ? "border-primary-600 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                  }`}
              >
                Personal Info
              </button>
              {canAssignLead() && (
                <button
                  type="button"
                  onClick={() => setDetailsSubTab("assign")}
                  className={`flex-1 py-2 text-center text-[13px] font-semibold border-b-[2px] transition-all duration-200 whitespace-nowrap ${detailsSubTab === "assign"
                    ? "border-primary-600 text-primary-600 dark:text-primary-400"
                    : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                    }`}
                >
                  Assign Users
                </button>
              )}
            </div>

            {detailsSubTab === "personal" ? (
              <LeadDetailsForm
                leadData={lead}
                isEditing={false}
                setIsEditing={() => { }}
                onOpenQuickLeadModal={() => setShowQuickLeadModal(true)}
                leadId={lead?.id || lead?.LeadId || lead?.leadId}
                hideDescription={true}
              />
            ) : (
              <div className="space-y-3 animate-in fade-in duration-300">
                {/* Assigned users list */}
                {ownersList && ownersList.length > 0 ? (
                  <div className="space-y-2">
                    {/* Label + inline edit button */}
                    <div className="flex items-center justify-between px-1">
                      <p className="text-[11px] font-semibold text-gray-500 dark:text-dark-400 uppercase tracking-wide">
                        Assigned Users
                      </p>
                      {canAssignLead() && (
                        <Button
                          onClick={() => setIsAssignOwnerModalOpen(true)}
                          variant="soft"
                          color="primary"
                          size="sm"
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs"
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                          Edit
                        </Button>
                      )}
                    </div>
                    <div className="rounded-xl border border-gray-200 dark:border-dark-700 overflow-hidden bg-white dark:bg-dark-800 divide-y divide-gray-100 dark:divide-dark-700">
                      {ownersList.map((owner, idx) => {
                        const name = owner.OwnerName || owner.name || owner.FullName || "Unknown";
                        const initials = name.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase();
                        const isPrimary = !!(owner.isPrimary || owner.IsPrimary);
                        return (
                          <div key={owner.OwnerId || owner.ownerId || idx} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-dark-700/50 transition-colors">
                            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-[11px] font-bold shrink-0 ${isPrimary ? "bg-primary-600 text-white" : "bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400"}`}>
                              {initials}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{name}</p>
                            </div>
                            {isPrimary && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 text-[10px] font-bold text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800 shrink-0">
                                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3">
                                  <path d="M5 16L3 5L8.5 10L12 4L15.5 10L21 5L19 16H5ZM19 19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V18H19V19Z" />
                                </svg>
                                Primary
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-dark-700 flex items-center justify-center mb-2">
                      <UserGroupIcon className="w-5 h-5 text-gray-400 opacity-60" />
                    </div>
                    <p className="text-sm font-medium text-gray-500 dark:text-dark-400 mb-3">No users assigned yet</p>
                    {canAssignLead() && (
                      <Button
                        onClick={() => setIsAssignOwnerModalOpen(true)}
                        variant="soft"
                        color="primary"
                        size="sm"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs"
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                        Assign Users
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Timeline Tab */}
        <div className={activeTab === "timeline" ? "block mt-0 space-y-2" : "hidden"}>
          <TimelineView
            leadId={lead?.id || lead?.LeadId || lead?.leadId}
            entityName="Lead"
            scrollTargetId="drawer-scroll-container"
            refreshTrigger={refreshTrigger}
            isActive={activeTab === "timeline"}
          />
        </div>

        {/* Contacts Tab */}
        <div className={activeTab === "contacts" ? "block mt-0 space-y-4" : "hidden"}>
          <ContactsTab leadId={lead?.id || lead?.LeadId || lead?.leadId} leadData={lead} onRefreshParent={() => setRefreshTrigger(prev => prev + 1)} />
        </div>

        {/* Client Notes Tab */}
        {visitedTabs.has("client-notes") && (
          <div className={activeTab === "client-notes" ? "block mt-0 space-y-4" : "hidden"}>
            <ClientNotesTab type="lead" leadId={leadId} isMobile={true} extraFieldsRequired={false} onRefreshParent={() => setRefreshTrigger(prev => prev + 1)} />
          </div>
        )}

        {/* Management / Activities / Communications — mobile only */}
        {isMobileView && (
          <>
            {visitedTabs.has("management") && (
              <div className={activeTab === "management" ? "block mt-2" : "hidden"}>
                <LeadManagementTab
                  isMobile={true}
                  leadData={lead}
                  progressData={{}}
                  onAssignOwner={() => setIsAssignOwnerModalOpen(true)}
                  onChangeStage={() => setShowStageModal(true)}
                  onSetPriority={() => setShowPriorityModal(true)}
                  onSetInteractionLevel={() => setShowInteractionLevelModal(true)}
                  onAddFollowUp={() => setIsFollowUpScheduleModal(true)}
                  onEditFollowUp={() => setIsFollowUpScheduleModal(true)}
                  onUpdateStatus={() => { }}
                  onAssignAcademic={() => { }}
                  onAssignAccountant={() => { }}
                  handleStepClick={() => { }}
                  setProgressData={() => { }}
                />
              </div>
            )}


            {visitedTabs.has("communications") && (
              <div className={activeTab === "communications" ? "block mt-2" : "hidden"}>
                <CommunicationsTab
                  leadId={leadId}
                  isMobile={true}
                />
              </div>
            )}
          </>
        )}

        {/* Documents Tab */}
        {visitedTabs.has("documents") && (
          <div className={activeTab === "documents" ? "block mt-0" : "hidden"}>
            <DocumentsTab
              leadId={leadId}
              isMobile={true}
              isStudent={lead?.IsStudent}
              leadData={lead}
              useDocumentsData={useDocumentsData}
              refreshProgressJourney={async () => {
                setRefreshTrigger(prev => prev + 1);
                await fetchLeadDetails(true, true);
                if (typeof onRefresh === "function") {
                  onRefresh();
                }
              }}
            />
          </div>
        )}

        {/* Activity Tab */}
        {visitedTabs.has("activity") && (
          <div className={activeTab === "activity" ? "block mt-0" : "hidden"}>
            <CalendarTab
              isMobile={true}
              leadData={lead}
              isStudent={lead?.IsStudent}
              initialDate={new URLSearchParams(window.location.search).get('date') || undefined}
            />
          </div>
        )}
      </div>

      {
        activeTab === "notes" && canEditLead() && (
          <div className="px-5 py-2.5 border-t border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700/50 flex shrink-0 transition-all duration-300 min-h-[64px] items-center justify-between">
            <div className="flex w-full gap-2 items-center">
              <div className="flex-1 relative">
                <textarea
                  onFocus={() => setIsAddingNote(true)}
                  onBlur={() => !newNoteText && setIsAddingNote(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmitNote();
                    }
                  }}
                  className="w-full p-2.5 text-sm border border-gray-300 dark:border-dark-500 focus:border-primary-500 dark:bg-dark-800 resize-none transition-all duration-300 outline-none rounded-xl shadow-sm bg-white dark:bg-dark-800 dark:text-dark-100 overflow-hidden"
                  placeholder="Add Note Here ..."
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  style={{ height: isAddingNote || newNoteText ? '52px' : '44px' }}
                />
              </div>

              {(isAddingNote || newNoteText) && (
                <div className="flex flex-row gap-1 items-center transform origin-right transition-all duration-300 ml-1">
                  {/* Important Toggle on the Right - ONLY show when text exists */}
                  {newNoteText.trim() && (
                    <button
                      onClick={() => setIsImportantForNewNote(!isImportantForNewNote)}
                      title={isImportantForNewNote ? "Marked as Important" : "Mark as Important"}
                      className={`p-1.5 rounded-lg transition-colors ${isImportantForNewNote ? 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-dark-600'}`}
                    >
                      {isImportantForNewNote ? <StarSolidIcon className="w-5 h-5" /> : <StarOutlineIcon className="w-5 h-5" />}
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setIsAddingNote(false);
                      setNewNoteText("");
                      setIsImportantForNewNote(false);
                    }}
                    title="Cancel"
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors rounded-lg"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                  {newNoteText.trim() && (
                    <button
                      onClick={handleSubmitNote}
                      disabled={isSubmittingNote}
                      title="Save Note"
                      className="p-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors rounded-lg flex items-center justify-center font-bold"
                    >
                      {isSubmittingNote ? (
                        <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent animate-spin rounded-full" />
                      ) : (
                        <PlusIcon className="w-6 h-6" />
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      }

      <CallModal
        isOpen={isCallModalOpen}
        onClose={() => setIsCallModalOpen(false)}
        leadData={{
          id: lead?.id || lead?.LeadId,
          name: leadContactsList?.[0]
            ? (leadContactsList[0].FullName || leadContactsList[0].fullName || leadContactsList[0].name)
            : (lead?.FullName || lead?.name || lead?.LeadName),
          mobile: leadContactsList?.[0]
            ? (leadContactsList[0].PhoneNumber || leadContactsList[0].phoneNumber || leadContactsList[0].phone || "")
            : (phoneNumber === "N/A" ? "" : phoneNumber),
          email: leadContactsList?.[0]
            ? (leadContactsList[0].Email || leadContactsList[0].email || "")
            : (emailAddress === "N/A" ? "" : emailAddress),
          source: "Manual Lead Form",
          stage: "New",
        }}
      />

      <EmailMessageModal
        isOpen={isEmailMessageModal}
        onClose={() => setIsEmailMessageModal(false)}
        leadData={{
          id: lead?.id || lead?.LeadId,
          name: leadContactsList?.[0]
            ? (leadContactsList[0].FullName || leadContactsList[0].fullName || leadContactsList[0].name)
            : (lead?.FullName || lead?.name || lead?.LeadName),
          title: leadTitle || leadName,
          mobile: leadContactsList?.[0]
            ? (leadContactsList[0].PhoneNumber || leadContactsList[0].phoneNumber || leadContactsList[0].phone || "")
            : (phoneNumber === "N/A" ? "" : phoneNumber),
          email: leadContactsList?.[0]
            ? (leadContactsList[0].Email || leadContactsList[0].email || "")
            : (emailAddress === "N/A" ? "" : emailAddress),
          source: "Manual Lead Form",
          stage: "New",
          contacts: leadContactsList || [],
        }}
      />

      <SMSMessageModal
        isOpen={isSmsMessageModal}
        onClose={() => setIsSmsMessageModal(false)}
        leadData={{
          id: lead?.id || lead?.LeadId,
          name: leadContactsList?.[0]
            ? (leadContactsList[0].FullName || leadContactsList[0].fullName || leadContactsList[0].name)
            : (lead?.FullName || lead?.name || lead?.LeadName),
          title: leadTitle || leadName,
          mobile: leadContactsList?.[0]
            ? (leadContactsList[0].PhoneNumber || leadContactsList[0].phoneNumber || leadContactsList[0].phone || "")
            : (phoneNumber === "N/A" ? "" : phoneNumber),
          email: leadContactsList?.[0]
            ? (leadContactsList[0].Email || leadContactsList[0].email || "")
            : (emailAddress === "N/A" ? "" : emailAddress),
          source: "Manual Lead Form",
          stage: "New",
          contacts: leadContactsList || [],
        }}
      />

      {/* <WhatsAppMessageModal
        isOpen={isWhatsAppMessageModal}
        onClose={() => setIsWhatsAppMessageModal(false)}
        leadData={{
          id: lead?.id || lead?.LeadId,
          name: lead?.FullName || lead?.name || lead?.LeadName,
          mobile: phoneNumber === "N/A" ? "" : phoneNumber,
          email: emailAddress === "N/A" ? "" : emailAddress,
          source: "Manual Lead Form",
          stage: "New",
        }}
      /> */}

      <FollowUpScheduleModal
        isOpen={isFollowUpScheduleModal}
        onClose={() => {
          setIsFollowUpScheduleModal(false);
          setSelectedActivityType(null);
        }}
        leadData={{
          ...lead,
          id: lead?.id || lead?.LeadId,
          name: lead?.FullName || lead?.name || lead?.LeadName,
          mobile: phoneNumber === "N/A" ? "" : phoneNumber,
          email: emailAddress === "N/A" ? "" : emailAddress,
          source: "Manual Lead Form",
          stage: "New",
          assignedTo: lead?.assignedTo || lead?.AssignedTo || lead?.LeadUserId,
          Owners: lead?.Owners
        }}
        preSelectedActivityType={selectedActivityType}
        onOwnerChange={() => {
          setRefreshTrigger(prev => prev + 1);
          if (onRefresh) onRefresh();
        }}
        onSuccess={() => {
          if (onRefresh) onRefresh();
          setRefreshTrigger(prev => prev + 1);
          const targetId = lead?.id || lead?.LeadId || lead?.leadId;
          invalidateTimelineCache(targetId);
        }}
        restrictedEditMode={false}
      />

      <QuickLeadModal
        isOpen={showQuickLeadModal}
        onClose={() => {
          setShowQuickLeadModal(false);
          setSelectedFormForApplication(null);
          setIsComingFromPaymentFlow(false);
        }}
        leadId={lead?.id || lead?.LeadId || lead?.leadId}
        leadData={lead}
        categories={lead?.LeadFieldCategories || []}
        selectedForm={{ ...selectedFormForApplication, FormId: lead?.LeadSourceFormId || selectedFormForApplication?.formId }}
        isApplicationForm={!!selectedFormForApplication}
        isComingFromPayment={isComingFromPaymentFlow}
        onLeadUpdated={() => {
          setRefreshTrigger(prev => prev + 1);
          if (onRefresh) onRefresh();
          invalidateTimelineCache(lead?.id || lead?.LeadId || lead?.leadId);

          // Close the drawer if we just finished an application form
          if (selectedFormForApplication && onClose) {
            onClose();
          }
          // Keep the drawer open after edit as per user request
        }}
      />

      <StageChangeModal
        isOpen={showStageModal}
        onClose={() => setShowStageModal(false)}
        leadData={{
          ...lead,
          id: lead?.id || lead?.LeadId || lead?.leadId,
          title: leadTitle || lead?.title,
          name: leadTitle || leadName,
          stage: stageName,
          stageConstant: stageConstant,
          subStage: substageName,
          subStageConstant: subStageConstant
        }}
        onStageChange={handleStageChangeRequest}
      />

      <ConfirmModal
        show={!!pendingJobCancelStageData}
        onClose={() => setPendingJobCancelStageData(null)}
        onOk={confirmJobCancellingStageChange}
        confirmLoading={isJobCancelStageChangeSubmitting}
        messages={{
          pending: {
            title: "Change Lead Stage & Cancel Job?",
            description: "This lead is converted to a job. Changing to this stage will update the associated job's status to Cancelled. Are you sure you want to continue?",
            actionText: "Yes, Proceed",
          },
        }}
      />

      <ConvertToJobModal
        show={showConvertToJobModal}
        onClose={() => setShowConvertToJobModal(false)}
        leadData={lead}
        leadContacts={leadContactsList}
        onReverted={() => {
          setLocalStage(null);
          setLocalSubStage(null);
          fetchLeadDetails(true, true);
          if (onRefresh) onRefresh();
        }}
      />

      <SetJobStatusModal
        show={showReactivateJobModal}
        onClose={handleCancelReactivate}
        jobId={reactivateJobId}
        currentStatusId={reactivateCurrentStatusId}
        excludeCancelled={true}
        onJobUpdated={() => {
          setPendingRevertLeadStageData(null);
          setShowReactivateJobModal(false);
          fetchLeadDetails(true, true);
          if (onRefresh) onRefresh();
        }}
      />

      <SetPriorityModal
        isOpen={showPriorityModal}
        onClose={() => setShowPriorityModal(false)}
        leadData={{
          ...lead,
          id: lead?.id || lead?.LeadId || lead?.leadId,
          title: leadTitle || lead?.title,
          name: leadTitle || leadName,
          priority: lead?.Priority || lead?.priority
        }}
        handlePriorityChange={(priority) => {
          const updateObj = typeof priority === 'object'
            ? priority
            : { priority: priority, Priority: priority };

          if (typeof priority === 'object') {
            setLocalPriority(priority.Priority || priority.priority);
          } else {
            setLocalPriority(priority);
          }

          setLeadDetails(prev => prev ? ({ ...prev, ...updateObj }) : prev);
          setRefreshTrigger(prev => prev + 1);
          if (onRefresh) onRefresh();
        }}
      />

      <SetInteractionLevelModal
        isOpen={showInteractionLevelModal}
        onClose={() => setShowInteractionLevelModal(false)}
        leadData={{
          ...lead,
          id: lead?.id || lead?.LeadId || lead?.leadId,
          title: leadTitle || lead?.title,
          name: leadTitle || leadName,
          InteractionLevelName: lead?.InteractionLevelName || lead?.LeadStatus
        }}
        handleInteractionLevelChange={(interactionLevel) => {
          const levelName = typeof interactionLevel === 'object' ? (interactionLevel.InteractionLevelName || interactionLevel.LeadStatus) : interactionLevel;
          setLocalInteractionLevel(levelName);
          setLeadDetails(prev => prev ? ({ ...prev, InteractionLevelName: levelName, LeadStatus: levelName }) : prev);
          setRefreshTrigger(prev => prev + 1);
          if (onRefresh) onRefresh();
        }}
      />


      <AssignOwnerModal
        isOpen={isAssignOwnerModalOpen}
        onClose={() => setIsAssignOwnerModalOpen(false)}
        leadData={{
          ...lead,
          id: lead?.id || lead?.LeadId || lead?.leadId,
          title: leadTitle || lead?.title,
          name: leadTitle || leadName,
          Owners: ownersList
        }}
        currentOwnerName={ownersList && ownersList.length > 0 ? ownersList.map(o => o.OwnerName || o.name || o.FullName).join(", ") : "Not Assigned"}
        onOwnerChange={async () => {
          fetchLeadDetails(true, true);
          if (onRefresh) onRefresh();
          setRefreshTrigger(prev => prev + 1);
        }}
      />
    </div >
  );
}
